import { translateBackend } from '@assets/i18n/config';
import {
  ASSET_CLASS_FIELD_BY_LEVEL,
  type AssetClassLevel,
} from '@interfaces/financialProduct';
import { isHouseholdModelPortfolioAssetClassStrategy } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.allocation.utils';
import type {
  HouseholdModelPortfolioChild,
  HouseholdModelPortfolioModel,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { round, sumBy } from 'lodash-es';

export type AllocationComparisonRow = {
  /** The security the weights on this row belong to. */
  financialProductId: string;
  name: string;
  ticker?: string;
  assetClassName?: string;
  /** Absent on a holding the household does not have today. */
  current?: number;
  /** Absent on a holding the change takes away. */
  proposed?: number;
  /**
   * The same two weights as a share of the whole model, which on a grouped
   * model is not what the weight itself says. Absent where the weight is, and
   * where the model cannot say what the weight is a share of.
   */
  currentShare?: number;
  proposedShare?: number;
};

const nameOf = (child: HouseholdModelPortfolioChild): string =>
  translateBackend(child.financialProduct?.translatedName) ||
  child.financialProduct?.name ||
  '';

const assetClassNameOf = (
  child: HouseholdModelPortfolioChild,
): string | undefined => {
  const assetClass =
    child.assetClass ?? child.financialProduct?.primaryAssetClass;
  return assetClass
    ? translateBackend(assetClass.translatedName) || assetClass.name
    : undefined;
};

const byProduct = (
  model: HouseholdModelPortfolioModel | undefined,
): Map<string, HouseholdModelPortfolioChild> =>
  new Map(
    (model?.children ?? [])
      .filter((child) => !!child.financialProduct?.id)
      .map((child) => [child.financialProduct?.id as string, child]),
  );

/**
 * What one point of a holding's weight comes to as a share of the whole model.
 *
 * A standard model's weights are read against the model itself, so a point is a
 * point. An asset class model's are read within their group, and what says how
 * much of the model a group answers for is that branch's target — so a holding
 * at 30% of a group targeted at 40% is 12% of the model.
 *
 * A holding whose group is not on the model, or whose group carries no target,
 * is left out: nothing on the model says what its weight is a share of, and a
 * figure stood in for it would be read as money.
 */
const modelShareScaleByProduct = (
  model: HouseholdModelPortfolioModel | undefined,
): Map<string, number> => {
  const children = (model?.children ?? []).filter(
    (child) => !!child.financialProduct?.id,
  );

  if (!isHouseholdModelPortfolioAssetClassStrategy(model)) {
    return new Map(
      children.map((child) => [child.financialProduct?.id as string, 1]),
    );
  }

  // The level the branches were drawn at is the one a holding is grouped by,
  // the same reading the group weights are checked against.
  const assetClassField =
    ASSET_CLASS_FIELD_BY_LEVEL[model?.assetClassLevel as AssetClassLevel] ??
    ASSET_CLASS_FIELD_BY_LEVEL.PRIMARY;
  const targetByAssetClass = new Map(
    (model?.assetClassBranches ?? [])
      .filter((branch) => !!branch.assetClass?.id)
      .map((branch) => [
        branch.assetClass?.id as string,
        branch.targetPercentage,
      ]),
  );

  const scales = new Map<string, number>();
  for (const child of children) {
    const assetClassId = child.financialProduct?.[assetClassField]?.id;
    const target = assetClassId
      ? targetByAssetClass.get(assetClassId)
      : undefined;
    if (target != null) {
      scales.set(child.financialProduct?.id as string, target / 100);
    }
  }

  return scales;
};

const shareOfModel = (
  percentage: number | undefined,
  scale: number | undefined,
): number | undefined =>
  percentage == null || scale == null ? undefined : percentage * scale;

/**
 * The two models as one list of holdings, so a weight can be read against the
 * weight it replaces rather than found again in a second table.
 *
 * The proposal leads, in the order the advisor left it. What the household
 * holds today and the change does not carry forward follows, weightless on the
 * proposed side — a holding being taken away is as much a part of the change as
 * one being added, and a reader who only saw the proposal would miss it.
 */
export const allocationComparisonRows = (
  proposed: HouseholdModelPortfolioModel,
  current?: HouseholdModelPortfolioModel,
): AllocationComparisonRow[] => {
  const held = byProduct(current);
  const proposedScale = modelShareScaleByProduct(proposed);
  const currentScale = modelShareScaleByProduct(current);
  const rows: AllocationComparisonRow[] = (proposed.children ?? [])
    .filter((child) => !!child.financialProduct?.id)
    .map((child) => {
      const financialProductId = child.financialProduct?.id as string;
      const today = held.get(financialProductId);

      return {
        financialProductId,
        name: nameOf(child) || (today ? nameOf(today) : ''),
        ticker: child.financialProduct?.ticker ?? undefined,
        assetClassName: assetClassNameOf(child),
        current: today?.percentage,
        proposed: child.percentage ?? 0,
        currentShare: shareOfModel(
          today?.percentage,
          currentScale.get(financialProductId),
        ),
        proposedShare: shareOfModel(
          child.percentage ?? 0,
          proposedScale.get(financialProductId),
        ),
      };
    });

  const proposedIds = new Set(rows.map((row) => row.financialProductId));

  return [
    ...rows,
    ...[...held.entries()]
      .filter(([financialProductId]) => !proposedIds.has(financialProductId))
      .map(([financialProductId, child]) => ({
        financialProductId,
        name: nameOf(child),
        ticker: child.financialProduct?.ticker ?? undefined,
        assetClassName: assetClassNameOf(child),
        current: child.percentage,
        proposed: undefined,
        currentShare: shareOfModel(
          child.percentage,
          currentScale.get(financialProductId),
        ),
        proposedShare: undefined,
      })),
  ];
};

/** What the change does to a holding, read from the two weights on its row. */
export type AllocationChange =
  | 'ADDED'
  | 'REMOVED'
  | 'INCREASED'
  | 'DECREASED'
  | 'UNCHANGED';

/**
 * What the proposal adds to a holding's weight, in percentage points.
 *
 * Rounded the way the table reads them, so a difference too small to be shown
 * is not one the advisor is asked to look for.
 */
export const allocationDelta = (row: AllocationComparisonRow): number =>
  round((row.proposed ?? 0) - (row.current ?? 0), 2);

export const allocationChange = (
  row: AllocationComparisonRow,
): AllocationChange => {
  if (row.current == null) return 'ADDED';
  if (row.proposed == null) return 'REMOVED';

  const delta = allocationDelta(row);
  if (delta > 0) return 'INCREASED';
  if (delta < 0) return 'DECREASED';
  return 'UNCHANGED';
};

export const isAllocationChanged = (row: AllocationComparisonRow): boolean =>
  allocationChange(row) !== 'UNCHANGED';

/** How many of the holdings the change actually touches. */
export const changedAllocationCount = (
  rows: AllocationComparisonRow[],
): number => rows.filter(isAllocationChanged).length;

/** What the weights on one side of the comparison come to. */
export const allocationTotal = (
  rows: AllocationComparisonRow[],
  side: 'current' | 'proposed',
): number =>
  round(
    sumBy(rows, (row) => row[side] ?? 0),
    2,
  );

/**
 * What a target weight is worth against the household's balance.
 *
 * A model carries weights and nothing else, so the money beside one is that
 * share of what the household holds today. Both sides of the comparison are
 * read against the same balance, which is what lets the two money columns be
 * read as a change rather than as two unrelated valuations.
 *
 * The weight has to be one read against the whole model: a grouped model's
 * holding weights are read within their group, and are a share of the balance
 * only once scaled by the group's own target.
 */
export const allocationValueCents = (
  percentage: number | undefined,
  householdMarketValueCents: number | undefined,
): number | undefined =>
  percentage == null || householdMarketValueCents == null
    ? undefined
    : Math.round((percentage / 100) * householdMarketValueCents);

/** The weight a side's money is read from, which is its share of the model. */
const SHARE_OF: Record<
  'current' | 'proposed',
  'currentShare' | 'proposedShare'
> = {
  current: 'currentShare',
  proposed: 'proposedShare',
};

/**
 * Share the household balance across a side's weights so the cents beside each
 * holding add up to the figure under the column.
 *
 * It is the share of the whole model that is read, not the weight the table
 * shows: on a grouped model those are only the same figure once the group's own
 * target is taken into account, and the weight alone would value every holding
 * as though its group were the entire portfolio.
 *
 * When the shares come to 100%, that figure is the balance itself — so the
 * Current balance line and the Current ($) total say the same thing. When they
 * do not, the total is only that share of the balance, which is what the column
 * is showing.
 */
export const allocationSideValueCents = (
  rows: AllocationComparisonRow[],
  side: 'current' | 'proposed',
  householdMarketValueCents: number | undefined,
): (number | undefined)[] => {
  if (householdMarketValueCents == null) {
    return rows.map(() => undefined);
  }

  const percentages = rows.map((row) => row[SHARE_OF[side]]);
  const totalPercentage = round(
    sumBy(percentages, (percentage) => percentage ?? 0),
    2,
  );
  const targetCents =
    totalPercentage === 100
      ? householdMarketValueCents
      : Math.round((totalPercentage / 100) * householdMarketValueCents);

  const exact = percentages.map((percentage) =>
    percentage == null
      ? undefined
      : (percentage / 100) * householdMarketValueCents,
  );
  const values = exact.map((value) =>
    value == null ? undefined : Math.floor(value),
  );
  let remaining = targetCents - sumBy(values, (value) => value ?? 0);

  const order = values
    .map((_value, index) => ({
      index,
      fraction:
        exact[index] == null ? -1 : exact[index]! - Math.floor(exact[index]!),
    }))
    .filter(({ index }) => values[index] != null)
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  if (remaining > 0) {
    for (const { index } of order) {
      if (remaining === 0) break;
      values[index] = (values[index] ?? 0) + 1;
      remaining -= 1;
    }
  } else if (remaining < 0) {
    for (const { index } of [...order].reverse()) {
      if (remaining === 0) break;
      values[index] = (values[index] ?? 0) - 1;
      remaining += 1;
    }
  }

  return values;
};

/**
 * What one side of the comparison is worth, totalled over the same figures the
 * column shows so the column adds up to what is under it.
 */
export const allocationTotalValueCents = (
  rows: AllocationComparisonRow[],
  side: 'current' | 'proposed',
  householdMarketValueCents: number | undefined,
): number | undefined =>
  householdMarketValueCents == null
    ? undefined
    : sumBy(
        allocationSideValueCents(rows, side, householdMarketValueCents),
        (value) => value ?? 0,
      );
